import React from "react";

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import { Controller, useForm } from "react-hook-form";

import { useAuth } from "shared/providers/auth";
import toast from "react-hot-toast";

import { IPrescription } from "shared/types/Prescription";
import { sendPrescription } from "requests/Prescription";
import { pdfDownload } from "requests/PdfDownload";
import { PostSubmitModal } from "../PostSubmitModal";

import SignatureCanvas from "react-signature-canvas";
import * as Styled from "./styles";
import { Schedule } from "shared/types/Schedule";

interface IProps {
  open: boolean;
  close: (open: boolean) => void;
  pacId: number;
  onCompleteOrto?: () => void;
  emailPatient?: string;
  patient?: Schedule;
}

const PrescriptionReferrals: React.FC<IProps> = ({
  open,
  close,
  pacId,
  onCompleteOrto,
  emailPatient,
  patient,
}) => {
  const { user } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    //reset,
  } = useForm<IPrescription>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const [showPostModal, setShowPostModal] = React.useState(false);
  const [idForm, setIdForm] = React.useState<number>(0);
  const [signatureDoctor, setSignatureDoctor] = React.useState<any>();
  const [signaturePatient, setSignaturePatient] = React.useState<any>();

  const sigCanvasPatient = React.useRef<SignatureCanvas>(null);

  const saveSignaturePatient = () => {
    if (sigCanvasPatient.current) {
      const dataURL = sigCanvasPatient.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      setSignaturePatient(dataURL);
    }
  };

  const sigCanvasDentist = React.useRef<SignatureCanvas>(null);

  const saveSignatureDentist = () => {
    if (sigCanvasDentist.current) {
      const dataURL = sigCanvasDentist.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      setSignatureDoctor(dataURL);
    }
  };

  const clearSignaturePatient = () => {
    sigCanvasPatient.current?.clear();
    setSignaturePatient(null);
  };

  const clearSignatureDentits = () => {
    sigCanvasDentist.current?.clear();
    setSignatureDoctor(null);
  };

  const onSubmit = async (data: IPrescription) => {
    if (!signaturePatient) {
      toast.error("Preencha a assinatura do paciente");
      return false;
    }
    if (!signatureDoctor) {
      toast.error("Preencha a assinatura do dentista");
      return false;
    }

    const fichaCompleta: IPrescription = {
      ...data,
      assinatura_paciente: signaturePatient,
      assinatura_dentista: signatureDoctor,
      paciente_id: pacId,
      para: patient?.pac_name as string,
      numero: String(patient?.pac_id_fant),
    };

    try {
      setLoading(true);
      const { status, data: dataForm } = await sendPrescription(
        fichaCompleta,
        user.data.id
      );

      if ([200, 201].includes(status)) {
        toast.success("Formulário enviado com sucesso");
        setIdForm(dataForm.data.NR_SEQ_CONTRATOFORM_CFOC as number);

        setLoading(false);
        setShowPostModal(true);
        onCompleteOrto?.();
      } else {
        setLoading(false);
        toast.error("Erro ao enviar formulário");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const onDownload = async () => {
    try {
      const { status, data } = await pdfDownload(
        { paciente_id: pacId, form_type: "receituario" },
        user.data.id
      );

      if ([200, 201].includes(status)) {
        const base64 = data.data;

        //@ts-ignore
        if (window && window.ReactNativeWebView) {
          //call rn to download
          //@ts-ignore
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "downloadBase64File",
              value: base64,
            })
          );
        } else {
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "receituario.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        toast.success("PDF baixado com sucesso");
      } else {
        toast.error(data.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao baixar o PDF");
    }
  };

  /*const clear = () => {
    reset();
  };*/

  return (
    <Modal open={open} onClose={() => close(false)}>
      <Box sx={{ backgroundColor: "white", height: "100%", width: "100%" }}>
        <Container maxWidth="xl" sx={{ overflow: "scroll", height: "100%" }}>
          <ReturnButton title="Receituário" onClick={close} />
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="uso"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Uso"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  error={!!errors.uso}
                  helperText={errors.uso?.message}
                />
              )}
            />

            <Controller
              name="prescricao"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Prescrição"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={6}
                  error={!!errors.prescricao}
                  helperText={errors.prescricao?.message}
                />
              )}
            />

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Paciente ou Responsável
                <Button onClick={clearSignaturePatient} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature">
                <SignatureCanvas
                  onEnd={saveSignaturePatient}
                  ref={sigCanvasPatient}
                  penColor="black"
                  canvasProps={{
                    height: 200,
                    className: "sigCanvas",
                    width: 680,
                  }}
                />
              </div>
            </Styled.BoxClinicalSignature>

            <Styled.BoxClinicalSignature className="boxValue">
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                Assinatura do Dentista
                <Button onClick={clearSignatureDentits} className="no-print">
                  Limpar
                </Button>
              </Typography>
              <div className="boxSignature">
                <SignatureCanvas
                  onEnd={saveSignatureDentist}
                  ref={sigCanvasDentist}
                  penColor="black"
                  canvasProps={{
                    height: 200,
                    className: "sigCanvas",
                    width: 680,
                  }}
                />
              </div>
            </Styled.BoxClinicalSignature>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 2, mb: 2 }}
            >
              {loading ? <CircularProgress color="inherit" /> : "Enviar"}
            </Button>
          </Box>
        </Container>

        <PostSubmitModal
          open={showPostModal}
          onClose={() => {
            setShowPostModal(false);
            //clear();
            close(false);
          }}
          onDownload={onDownload}
          onSendWhatsApp={() => {
            toast.success("Link enviado no WhatsApp");
          }}
          defaultEmail={emailPatient as string}
          formId={idForm}
          userId={user.data.id}
          userWpp={""}
        />
      </Box>
    </Modal>
  );
};

export { PrescriptionReferrals };
